import { AfterContentChecked, Component, ElementRef, EventEmitter,
         Inject, Input, Output, Renderer2
       } from '@angular/core';

import { ColorPalette, ColorPaletteService } from './color-palette.service';

type Rgb = [number, number, number];

@Component({
    selector: 'color-chooser',
    templateUrl: './color-chooser.component.html',
    styleUrls: ['./color-chooser.component.scss'],
    standalone: false
})
export class ColorChooserComponent implements AfterContentChecked
{
  private static readonly MIN_CONTRAST_RATIO: number = 1.5;
  private static readonly MAX_DARK_LUMINANCE: number = 0.179;

  @Output() public currentColorChange: EventEmitter<string> =
      new EventEmitter();

  @Input() public currentColor: string = '#666';
  @Input() public transparentAllowed: boolean = false;
  @Input() public contrastColor: string|undefined = undefined;
  @Input() public appliesToBackground: boolean = false;

  public colors: ColorPalette = {
    baseColors: [],
    additionalColors: [],
  };
  public lowContrastColors: Set<string> = new Set();
  public darkColors: Set<string> = new Set();

  constructor(
      private element: ElementRef,
      @Inject(Renderer2) private renderer: Renderer2,
      @Inject(ColorPaletteService)
          public colorPaletteService: ColorPaletteService)
  { }

  public ngAfterContentChecked(): void
  {
    this.colors.baseColors = [];
    if (this.transparentAllowed) {
      this.colors.baseColors.push('transparent');
    }
    this.colors.baseColors.push(
        ...this.colorPaletteService.colorPalette.baseColors);
    this.colors.additionalColors =
        this.colorPaletteService.colorPalette.additionalColors;
    const allColors =
        [...this.colors.additionalColors, ...this.colors.baseColors];
    this.lowContrastColors = this.findLowContrastColors(allColors);
    this.darkColors = ColorChooserComponent.findDarkColors(allColors);
  }

  public setColor(color: string): void
  {
    if (this.lowContrastColors.has(color)) {
      return;
    }
    this.currentColor = color;
    this.currentColorChange.emit(color);
  }

  private findLowContrastColors(colors: Array<string>): Set<string>
  {
    const contrastRgb = this.contrastColor
        ? ColorChooserComponent.parseColor(this.contrastColor)
        : undefined;
    if (!contrastRgb) {
      return new Set();
    }
    const contrastLuminance =
        ColorChooserComponent.relativeLuminance(contrastRgb);
    return new Set(colors.filter(color => {
      const rgb = ColorChooserComponent.parseColor(color);
      if (!rgb) {
        return false;
      }
      const luminance = ColorChooserComponent.relativeLuminance(rgb);
      const ratio = (Math.max(luminance, contrastLuminance) + 0.05)
          / (Math.min(luminance, contrastLuminance) + 0.05);
      return ratio < ColorChooserComponent.MIN_CONTRAST_RATIO;
    }));
  }

  private static findDarkColors(colors: Array<string>): Set<string>
  {
    return new Set(colors.filter(color => {
      const rgb = ColorChooserComponent.parseColor(color);
      return rgb !== undefined
          && ColorChooserComponent.relativeLuminance(rgb)
              < ColorChooserComponent.MAX_DARK_LUMINANCE;
    }));
  }

  private static parseColor(color: string): Rgb|undefined
  {
    const rgbMatch =
        /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(color);
    if (rgbMatch) {
      return rgbMatch.slice(1, 4).map(Number) as Rgb;
    }
    const digits = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color)?.[1];
    if (!digits) {
      return undefined;
    }
    const hex = digits.length === 3
        ? digits.split('').map(c => c + c).join('')
        : digits;
    return [0, 2, 4].map(i => parseInt(hex.substring(i, i + 2), 16)) as Rgb;
  }

  // WCAG 2.x.
  private static relativeLuminance(rgb: [number, number, number]): number
  {
    const [r, g, b] = rgb.map(channel => {
      const c = channel / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }
}
